import { useEffect, useRef, useState } from "react";
import { runCommand, ROOT } from "../utils/Commands";
import { PROFILE } from "../data/portfolio";

const BOOT_LINES = [
  "Last login: today on ttys000",
  `booting ${PROFILE.host}...`,
  "loading profile... done",
];

export default function useTerminalEngine() {
  const [lines, setLines] = useState([]); // { id, type, text?, cmdText?, output? }
  const [input, setInput] = useState("");
  const [cwd, setCwd] = useState(ROOT);
  const [cmdHistory, setCmdHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [booted, setBooted] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const idRef = useRef(0);
  const nextId = () => (idRef.current += 1);

  // boot sequence, runs once
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i < BOOT_LINES.length) {
        setLines((prev) => [...prev, { id: nextId(), type: "boot-line", text: BOOT_LINES[i] }]);
        i += 1;
      } else {
        clearInterval(timer);
        setLines((prev) => [...prev, { id: nextId(), type: "banner" }]);
        setBooted(true);
      }
    }, 220);
    return () => clearInterval(timer);
  }, []);

  // auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  const execute = (raw) => {
    const cmd = raw.trim();
    if (cmd === "") return;

    const { output, newCwd, clearScreen } = runCommand(cmd, cwd);

    if (clearScreen) {
      setLines([]);
      return;
    }
    if (newCwd) setCwd(newCwd);

    setLines((prev) => [
      ...prev,
      { id: nextId(), type: "cmd", cmdText: cmd, cwdAtRun: cwd, output },
    ]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      execute(input);
      setCmdHistory((prev) => [...prev, input]);
      setHistoryIndex(-1);
      setInput("");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length === 0) return;
      const newIndex = historyIndex === -1 ? cmdHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInput(cmdHistory[newIndex]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === -1) return;
      const newIndex = historyIndex + 1;
      if (newIndex >= cmdHistory.length) {
        setHistoryIndex(-1);
        setInput("");
      } else {
        setHistoryIndex(newIndex);
        setInput(cmdHistory[newIndex]);
      }
    }
  };

  return {
    lines,
    input,
    setInput,
    cwd,
    booted,
    scrollRef,
    inputRef,
    handleKeyDown,
  };
}